// useOpenRouterCredits — periodic fetch of /providers/openrouter/credits,
// shared across mounted components via a tiny pub/sub.
//
// Server already TTL-caches for 5 min, so the client refreshes on the same
// cadence: one fetch on mount, then again every 5 min while the tab is open.
// Errors are non-fatal — the hook returns the last good payload (if any) and
// the latest error string; the header pill renders either depending on state.
//
// Both `credits` and `error` are module-level so background refresh results
// (from the setInterval timer) propagate to every mounted hook instance. A
// successful refresh clears `cachedError`; a failed refresh updates it and
// notifies subscribers. Without this, a one-off failure stuck the local
// error state forever and later background successes were invisible.

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import type { OpenRouterCreditsResponse } from '../lib/types';

const REFRESH_MS = 5 * 60 * 1000;

let cached: OpenRouterCreditsResponse | null = null;
let cachedError: string | null = null;
let inflight: Promise<OpenRouterCreditsResponse> | null = null;
let inflightController: AbortController | null = null;
let lastFetchedAt = 0;
let timer: ReturnType<typeof setInterval> | null = null;

interface CreditsSnapshot {
  credits: OpenRouterCreditsResponse | null;
  error: string | null;
}

const subscribers = new Set<(snap: CreditsSnapshot) => void>();

function notify(): void {
  const snap: CreditsSnapshot = { credits: cached, error: cachedError };
  for (const sub of subscribers) sub(snap);
}

function fetchOnce(): Promise<OpenRouterCreditsResponse> {
  if (inflight) return inflight;
  const controller = new AbortController();
  inflightController = controller;
  inflight = api.getOpenRouterCredits(controller.signal).then((c) => {
    cached = c;
    cachedError = null;
    lastFetchedAt = Date.now();
    inflight = null;
    inflightController = null;
    notify();
    return c;
  }).catch((err) => {
    inflight = null;
    inflightController = null;
    cachedError = err instanceof Error ? err.message : String(err);
    notify();
    throw err;
  });
  return inflight;
}

function ensureTimer(): void {
  if (timer !== null) return;
  timer = setInterval(() => {
    fetchOnce().catch(() => { /* swallowed — cachedError + notify already fired */ });
  }, REFRESH_MS);
}

function teardownIfIdle(): void {
  if (subscribers.size === 0) {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    if (inflightController !== null) {
      inflightController.abort();
      inflightController = null;
    }
  }
}

// Test seam.
export function __resetOpenRouterCreditsCache(): void {
  cached = null;
  cachedError = null;
  inflight = null;
  lastFetchedAt = 0;
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  if (inflightController !== null) {
    inflightController.abort();
    inflightController = null;
  }
  subscribers.clear();
}

export interface UseOpenRouterCreditsResult {
  credits: OpenRouterCreditsResponse | null;
  error: string | null;
}

export function useOpenRouterCredits(): UseOpenRouterCreditsResult {
  const [credits, setCredits] = useState<OpenRouterCreditsResponse | null>(cached);
  const [error, setError] = useState<string | null>(cachedError);

  useEffect(() => {
    let active = true;
    const onUpdate = (snap: CreditsSnapshot): void => {
      if (!active) return;
      setCredits(snap.credits);
      setError(snap.error);
    };
    subscribers.add(onUpdate);
    ensureTimer();

    const stale = Date.now() - lastFetchedAt > REFRESH_MS;
    if (!cached || stale) {
      fetchOnce().catch(() => { /* surfaced via notify → setError */ });
    }

    return () => {
      active = false;
      subscribers.delete(onUpdate);
      teardownIfIdle();
    };
  }, []);

  return { credits, error };
}
